import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { employeeReferralInfo } from "@/api";
import {
  Users, FileText, Banknote, UserCog, BarChart3, ShieldCheck,
  DollarSign, Ticket, ArrowRight, Sparkles,
} from "lucide-react";

const PERMISSION_SHORTCUTS = [
  { perm: "view_customers",     path: "users",         label: "Customers",     icon: Users,       accent: "from-blue-500 to-blue-600" },
  { perm: "verify_kyc",         path: "aadhaar-queue", label: "KYC / Aadhaar", icon: ShieldCheck, accent: "from-emerald-500 to-emerald-600" },
  { perm: "view_applications",  path: "applications",  label: "Applications",  icon: FileText,    accent: "from-violet-500 to-violet-600" },
  { perm: "view_disbursements", path: "disbursements", label: "Disbursements", icon: Banknote,    accent: "from-amber-500 to-amber-600" },
  { perm: "view_agents",        path: "agents",        label: "Agents",        icon: UserCog,     accent: "from-pink-500 to-pink-600" },
  { perm: "view_commissions",   path: "commissions",   label: "Commissions",   icon: DollarSign,  accent: "from-teal-500 to-teal-600" },
  { perm: "view_reports",       path: "reports",       label: "Reports",       icon: BarChart3,   accent: "from-indigo-500 to-indigo-600" },
];

export default function EmployeeOverview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const permissions = user?.permissions || {};
  const [referral, setReferral] = useState({ referral_code: null, total_agents_referred: 0 });

  useEffect(() => {
    (async () => {
      try {
        const res = await employeeReferralInfo();
        setReferral({
          referral_code: res.data?.referral_code || null,
          total_agents_referred: res.data?.total_agents_referred || 0,
        });
      } catch {
        // referral info is non-critical here
      }
    })();
  }, []);

  const visibleShortcuts = PERMISSION_SHORTCUTS.filter(s => permissions[s.perm]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employee-overview">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#1565C0] via-[#0D47A1] to-[#0A3A8A] rounded-2xl p-6 sm:p-8 text-white shadow-lg">
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-2 opacity-80">
            <Sparkles className="h-4 w-4" />
            <span className="text-xs uppercase tracking-[0.25em] font-bold">Welcome back</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            {user?.name || "Employee"}
          </h1>
          <p className="text-sm opacity-90 mt-1 font-mono">{user?.employee_id || ""}</p>
        </div>
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-12 -left-12 w-56 h-56 rounded-full bg-white/5 blur-3xl pointer-events-none" />
      </div>

      {/* Referral summary card */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-2">
            <Ticket className="h-4 w-4 text-[#1565C0]" />
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">My Referral Code</span>
          </div>
          {referral.referral_code ? (
            <code
              className="inline-block text-2xl font-black font-mono bg-blue-50 text-[#0D47A1] border border-blue-100 px-4 py-2 rounded-xl tracking-wider"
              data-testid="employee-overview-referral-code"
            >
              {referral.referral_code}
            </code>
          ) : (
            <p className="text-sm text-slate-500">Not assigned yet</p>
          )}
          <button
            onClick={() => navigate('/employee/my-referrals')}
            className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#1565C0] hover:underline"
            data-testid="employee-overview-go-referrals"
          >
            View all referrals <ArrowRight className="h-3 w-3" />
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-2">
            <UserCog className="h-4 w-4 text-emerald-600" />
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Agents Referred</span>
          </div>
          <p
            className="text-4xl font-black text-[#0A2540]"
            style={{ fontFamily: "Outfit, sans-serif" }}
            data-testid="employee-overview-total-referred"
          >
            {referral.total_agents_referred || 0}
          </p>
          <p className="text-xs text-slate-500 mt-1">Total agents onboarded using your code</p>
        </div>
      </div>

      {/* Permission-gated shortcuts */}
      <div>
        <h2 className="text-lg font-black text-[#0A2540] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
          Your workspace
        </h2>
        {visibleShortcuts.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
            <p className="text-sm text-slate-500">
              You don't have any section permissions yet. Please contact your administrator.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {visibleShortcuts.map(s => {
              const Icon = s.icon;
              return (
                <button
                  key={s.path}
                  onClick={() => navigate(`/employee/${s.path}`)}
                  data-testid={`shortcut-${s.path}`}
                  className="group bg-white border border-gray-100 hover:border-[#1565C0]/40 rounded-2xl p-4 shadow-sm hover:shadow-md text-left transition-all"
                >
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${s.accent} flex items-center justify-center shadow-sm mb-3`}>
                    <Icon className="h-5 w-5 text-white" strokeWidth={1.8} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-[#0A2540]">{s.label}</span>
                    <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-[#1565C0] transition-colors" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
